import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  getEducations,
  createEducation,
  updateEducation,
  deleteEducation
} from '../../../services/candidate/educationService';

const Education = ({ profileData, onUpdate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [educations, setEducations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);
  const [formData, setFormData] = useState({
    instituteName: '',
    degree: '',
    fieldOfStudy: '',
    startDate: '',
    endDate: '',
    currentlyStudying: false,
    grade: '',
    location: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch education data on component mount
  useEffect(() => {
    fetchEducations();
  }, []);

  const fetchEducations = async () => {
    try {
      setLoading(true);
      const response = await getEducations();
      
      if (response.success) {
        setEducations(response.data || []);
      } else {
        toast.error('Failed to load education data');
      }
    } catch (error) {
      console.error('Error fetching education:', error);
      toast.error('Error loading education data');
    } finally {
      setLoading(false);
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    const today = new Date().toISOString().split('T')[0];

    // Institute Name validation
    if (!formData.instituteName.trim()) {
      errors.instituteName = 'Institution name is required';
    } else if (formData.instituteName.length < 2) {
      errors.instituteName = 'Institution name must be at least 2 characters';
    } else if (formData.instituteName.length > 200) {
      errors.instituteName = 'Institution name must be less than 200 characters';
    }

    // Degree validation
    if (!formData.degree.trim()) {
      errors.degree = 'Degree is required';
    } else if (formData.degree.length < 2) {
      errors.degree = 'Degree must be at least 2 characters';
    } else if (formData.degree.length > 150) {
      errors.degree = 'Degree must be less than 150 characters';
    }

    // Field of Study validation
    if (!formData.fieldOfStudy.trim()) {
      errors.fieldOfStudy = 'Field of study is required';
    } else if (formData.fieldOfStudy.length < 2) {
      errors.fieldOfStudy = 'Field of study must be at least 2 characters';
    } else if (formData.fieldOfStudy.length > 150) {
      errors.fieldOfStudy = 'Field of study must be less than 150 characters';
    }

    // Start Date validation
    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    } else if (new Date(formData.startDate) > new Date()) {
      errors.startDate = 'Start date cannot be in the future';
    }

    // End Date validation (only if not currently studying)
    if (!formData.currentlyStudying) {
      if (!formData.endDate) {
        errors.endDate = 'End date is required when not currently studying';
      } else if (new Date(formData.endDate) < new Date(formData.startDate)) {
        errors.endDate = 'End date cannot be before start date';
      } else if (new Date(formData.endDate) > new Date()) {
        errors.endDate = 'End date cannot be in the future';
      }
    }

    // Grade validation (optional)
    if (formData.grade && formData.grade.length > 50) {
      errors.grade = 'Grade must be less than 50 characters';
    }

    // Location validation (optional)
    if (formData.location && formData.location.length > 200) {
      errors.location = 'Location must be less than 200 characters';
    }

    // Description validation (optional)
    if (formData.description && formData.description.length > 1000) {
      errors.description = 'Description must be less than 1000 characters';
    }

    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }

    // If currentlyStudying is checked, clear endDate
    if (name === 'currentlyStudying' && checked) {
      setFormData(prev => ({ ...prev, endDate: '' }));
      if (formErrors.endDate) {
        setFormErrors(prev => ({ ...prev, endDate: '' }));
      }
    }
  };

  const openModal = (education = null) => {
    if (education) {
      setEditingEducation(education);
      setFormData({
        instituteName: education.instituteName || '',
        degree: education.degree || '',
        fieldOfStudy: education.fieldOfStudy || '',
        startDate: education.startDate ? education.startDate.split('T')[0] : '',
        endDate: education.endDate ? education.endDate.split('T')[0] : '',
        currentlyStudying: education.currentlyStudying || false,
        grade: education.grade || '',
        location: education.location || '',
        description: education.description || ''
      });
    } else {
      setEditingEducation(null);
      setFormData({
        instituteName: '',
        degree: '',
        fieldOfStudy: '',
        startDate: '',
        endDate: '',
        currentlyStudying: false,
        grade: '',
        location: '',
        description: ''
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);

    try {
      // Prepare payload
      const payload = {
        instituteName: formData.instituteName.trim(),
        degree: formData.degree.trim(),
        fieldOfStudy: formData.fieldOfStudy.trim(),
        startDate: formData.startDate,
        currentlyStudying: formData.currentlyStudying,
        grade: formData.grade.trim() || undefined,
        location: formData.location.trim() || undefined,
        description: formData.description.trim() || undefined
      };

      // Add endDate only if not currently studying
      if (!formData.currentlyStudying) {
        payload.endDate = formData.endDate;
      }

      let response;
      if (editingEducation) {
        // Update existing education
        response = await updateEducation(editingEducation._id, payload);
      } else {
        // Create new education
        response = await createEducation(payload);
      }

      if (response.success) {
        toast.success(response.message || 'Education saved successfully!');
        setIsModalOpen(false);
        fetchEducations(); // Refresh the list
        
        // Trigger parent update for profile completion
        if (onUpdate) {
          onUpdate();
        }
      } else {
        toast.error(response.message || 'Failed to save education');
      }
    } catch (error) {
      console.error('Error saving education:', error);
      //toast.error('Error saving education. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    
    try {
      const response = await deleteEducation(id);
      
      if (response.success) {
        toast.success(response.message || 'Education deleted successfully!');
        setEducations(prev => prev.filter(edu => edu._id !== id));
        
        // Trigger parent update for profile completion
        if (onUpdate) {
          onUpdate();
        }
      } else {
        toast.error(response.message || 'Failed to delete education');
      }
    } catch (error) {
      console.error('Error deleting education:', error);
      toast.error('Error deleting education. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="education">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Manage your educational background</h3>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 btn-primary text-white px-4 py-2 rounded-lg transition-colors"
        >
          <i className="fas fa-plus"></i>
          Add Education
        </button>
      </div>

      <div className="space-y-4">
        {educations.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <i className="fas fa-graduation-cap text-4xl text-gray-400 mb-3"></i>
            <p className="text-gray-600">No education added yet</p>
            <p className="text-gray-500 text-sm mt-1">Add your educational qualifications to showcase your academic journey</p>
          </div>
        ) : (
          educations.map((education) => (
            <div key={education._id} className="border border-gray-200 rounded-lg p-5 hover:border-gray-300 transition-colors">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-800 text-lg">{education.degree}</h4>
                    {education.currentlyStudying && (
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        Currently Studying
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-gray-600 mt-1">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-university text-sm text-gray-400"></i>
                      <span>{education.instituteName}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-gray-600 mt-1">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-book text-sm text-gray-400"></i>
                      <span>{education.fieldOfStudy}</span>
                    </div>
                  </div>
                  
                  {education.location && (
                    <div className="flex items-center gap-4 text-gray-600 mt-1">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-map-marker-alt text-sm text-gray-400"></i>
                        <span>{education.location}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-gray-500 text-sm mt-2">
                    <i className="fas fa-calendar text-sm text-gray-400"></i>
                    <span>
                      {new Date(education.startDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short' 
                      })} -{' '}
                      {education.currentlyStudying 
                        ? 'Present' 
                        : new Date(education.endDate).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short' 
                          })
                      }
                      {education.grade && (
                        <>
                          <span className="mx-2">•</span>
                          <span>Grade: {education.grade}</span>
                        </>
                      )}
                    </span>
                  </div>
                  
                  {education.description && (
                    <p className="text-gray-600 mt-3 text-sm leading-relaxed">{education.description}</p>
                  )}
                </div>
                
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => openModal(education)}
                    className="flex items-center gap-2 bg-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                    title="Edit"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    onClick={() => handleDelete(education._id)}
                    className="flex items-center gap-2 bg-red-100 text-red-700 px-3 py-2 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">
                  {editingEducation ? 'Edit Education' : 'Add Education'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Institute Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Institution Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="instituteName"
                    value={formData.instituteName}
                    onChange={handleInputChange}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.instituteName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Bengaluru University"
                  />
                  {formErrors.instituteName && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.instituteName}</p>
                  )}
                </div>

                {/* Degree */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Degree <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="degree"
                    value={formData.degree}
                    onChange={handleInputChange}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.degree ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Bachelor of Science"
                  />
                  {formErrors.degree && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.degree}</p>
                  )}
                </div>

                {/* Field of Study */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field of Study <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fieldOfStudy"
                    value={formData.fieldOfStudy}
                    onChange={handleInputChange}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.fieldOfStudy ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Medical Science"
                  />
                  {formErrors.fieldOfStudy && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.fieldOfStudy}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        formErrors.startDate ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.startDate && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.startDate}</p>
                    )}
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date {!formData.currentlyStudying && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      disabled={formData.currentlyStudying}
                      className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        formErrors.endDate ? 'border-red-500' : 'border-gray-300'
                      } ${formData.currentlyStudying ? 'bg-gray-100' : ''}`}
                    />
                    {formErrors.endDate && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.endDate}</p>
                    )}
                  </div>
                </div>

                {/* Currently Studying */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="currentlyStudying"
                    name="currentlyStudying"
                    checked={formData.currentlyStudying}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="currentlyStudying" className="ml-2 text-sm text-gray-700">
                    I currently study here
                  </label>
                </div>

                {/* Grade */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grade
                  </label>
                  <input
                    type="text"
                    name="grade"
                    value={formData.grade}
                    onChange={handleInputChange}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.grade ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: 3.8 GPA, First Class, etc."
                  />
                  {formErrors.grade && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.grade}</p>
                  )}
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.location ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Bengaluru, Karnataka"
                  />
                  {formErrors.location && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.location}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Describe your academic achievements, projects, or specializations..."
                  />
                  <div className="flex justify-between items-center mt-1">
                    {formErrors.description && (
                      <p className="text-sm text-red-500">{formErrors.description}</p>
                    )}
                    <span className={`text-xs ml-auto ${
                      formData.description.length > 1000 ? 'text-red-500' : 'text-gray-500'
                    }`}>
                      {formData.description.length}/1000
                    </span>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 btn-primary text-white rounded-lg flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        {editingEducation ? 'Updating...' : 'Adding...'}
                      </>
                    ) : (
                      editingEducation ? 'Update Education' : 'Add Education'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Education;